import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import { createAssetClassFilterFields } from "@/features/FilterTable/assetClassFilterFields";
import { preprocessTextAsyncAutocompleteField } from "@/features/FormCard/utils";
import { createCurrencyOption } from "@/hooks/useCurrencyOption";
import { z } from "zod";
import { DEFAULT_ASSET_CLASS_OPTIONS } from "./edit-form-conf";
import { DepField, makeFiltersBuilder } from "@/features/FilterTable/utils";
import { Clients } from "@/lib/clients";
import { useTranslation } from "react-i18next";

import { Tables } from "@/gen/tableNames/tables";

const TABLE_NAME = Tables.D1_STATIC_DATA;

const filterD1StaticDatumsSchema = z.object({
  name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  isin_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  code_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  ticker_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  currency_contains: z.string().optional(),
  asset_class_1_equals: z.string().optional(),
  asset_class_2_equals: z.string().optional(),
});

export type FilterD1StaticDatumsSchema = z.infer<
  typeof filterD1StaticDatumsSchema
>;

const assetDepFields: readonly DepField<FilterD1StaticDatumsSchema>[] = [
  {
    name: "name_contains",
    key: "id",
    extract: "id",
    bucket: "equals",
  },
  { name: "isin_contains", key: "isin", extract: "text", bucket: "equals" },
  { name: "code_contains", key: "code", extract: "text", bucket: "equals" },
  {
    name: "ticker_contains",
    key: "ticker",
    extract: "text",
    bucket: "contains",
  },
];

export function FilterD1StaticDatums() {
  const { t, i18n } = useTranslation("tables");

  const fields: Field[] = [
    {
      name: "name_contains",
      label: t(`${TABLE_NAME}.searchbox.name_contains`, { defaultValue: "Name" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.D1}/${Tables.D1_STATIC_DATA}`,
      column_name: "name",
      filters: makeFiltersBuilder<FilterD1StaticDatumsSchema>(
        assetDepFields,
        "name_contains",
      ),
    },
    {
      name: "isin_contains",
      label: t(`${TABLE_NAME}.searchbox.isin_contains`, { defaultValue: "ISIN" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.D1}/${Tables.D1_STATIC_DATA}`,
      column_name: "isin",
      filters: makeFiltersBuilder<FilterD1StaticDatumsSchema>(
        assetDepFields,
        "isin_contains",
      ),
    },
    {
      name: "code_contains",
      label: t(`${TABLE_NAME}.searchbox.code_contains`, { defaultValue: "Code" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.D1}/${Tables.D1_STATIC_DATA}`,
      column_name: "code",
      filters: makeFiltersBuilder<FilterD1StaticDatumsSchema>(
        assetDepFields,
        "code_contains",
      ),
    },
    {
      name: "ticker_contains",
      label: t(`${TABLE_NAME}.searchbox.ticker_contains`, { defaultValue: "Ticker" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.D1}/${Tables.D1_STATIC_DATA}`,
      column_name: "ticker",
      filters: makeFiltersBuilder(assetDepFields, "ticker_contains"),
    },
    {
      name: "currency_contains",
      label: t(`${TABLE_NAME}.searchbox.currency_contains`, { defaultValue: "Currency" }),
      type: "autocomplete",
      options: createCurrencyOption(),
    },
    ...createAssetClassFilterFields({
      assetClassFieldName: "asset_class_1_equals",
      assetSubclassFieldName: "asset_class_2_equals",
      assetClassLabel: t(`${TABLE_NAME}.searchbox.asset_class_1_equals`, { defaultValue: "Asset class" }),
      assetSubclassLabel: t(`${TABLE_NAME}.searchbox.asset_class_2_equals`, { defaultValue: "Sub asset class" }),
      client: Clients.D1,
      staticDataTableName: Tables.D1_STATIC_DATA,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      assetSubclassOptions: DEFAULT_ASSET_CLASS_OPTIONS,
    }),
  ];

  return (
    <FilterTable
      schema={filterD1StaticDatumsSchema}
      client={Clients.D1}
      tableName={Tables.D1_STATIC_DATA}
      fields={fields}
    />
  );
}
